import { Controller, useFormContext } from "@ui/components/Form";
import { MarkdownWrapper } from "@ui/components/MarkdownWrapper";
import { useTranslations } from "@ui/providers/useTranslations";
import { replaceOneTrustPlaceholdersWithLinks } from "@ui/utils/linkReplacer/linkReplacer";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { RadioButton, RadioButtonGroup } from "@jti/core-components";
import type { GroupedPurposes, OtPurpose, OtPurposeByUUID } from "@jti/fe-common";

import styles from "./ConsentSection.module.scss";

const { sanitize } = dompurify;

export interface GroupedPurposesBySection {
  purposeTitle: string;
  purposeDescription?: string | null;
  purposeId: string;
  optionsList?: readonly string[];
  purposes: (Omit<OtPurpose | OtPurposeByUUID, "default"> & { default: boolean | string | null })[];
}

export type ConsentOptionsListType = "default" | "extended";

const MULTIOPTION_SEPARATOR = "--";
const DEFAULT_TRANSLATIONS_PREFIX = "profile.communication.manage_consent";
const DEFAULT_OPTIONS_LIST = ["yes", "no"] as const;
const EXTENDED_OPTIONS_LIST = ["yes.until", "yes.one_off", "no.one_off"] as const;

type OptionsListKey = (typeof DEFAULT_OPTIONS_LIST)[number] | (typeof EXTENDED_OPTIONS_LIST)[number];

type BooleanString = "true" | "false";
export type OptionsListValue = BooleanString | `${BooleanString}--${BooleanString}`;
type OptionsListMapper = Record<OptionsListKey, OptionsListValue>;

/**
 * Maps consent option keys to a combined string value.
 *
 * Radio groups can only hold one value. To work around this, we combine the
 * "accepted" and "one_off" properties into a single string.  This string is
 * then used as the radio group's value.
 *
 * The string format is "accepted--one_off".
 *   - accepted:  "true" or "false"
 *   - one_off: "true" or "false"
 *
 * Example: "true--false" means accepted=true, one_off=false.
 *
 * NOTE: Before sending to the API, this combined string is split back into
 * its separate "accepted" and "one_off" values. See:
 * frontend-libs/packages/user-auth/src/pages/Registration/NewRegistrationForm/utils/transformToCasStructure/transformToCasConsents.ts
 * function transformToCasConsents
 */

const optionsMapper: OptionsListMapper = {
  yes: "true",
  no: "false",
  "yes.until": "true--false",
  "yes.one_off": "true--true",
  "no.one_off": "false--false",
};

export const PurposeMarkdownText = ({ text, className, id }: { text: string; className?: string; id?: string }) => {
  const { ONE_TRUST_DYNAMIC_LINKS: otdl } = useConfig();

  if (!text.trim()) return null;

  return (
    <div className={className} id={id}>
      <MarkdownWrapper>
        {sanitize(otdl ? replaceOneTrustPlaceholdersWithLinks(text, otdl) : text, { ADD_ATTR: ["target"] })}
      </MarkdownWrapper>
    </div>
  );
};

export const mapSectionWithExtendedOptions = ({
  sections,
  resetDefaultValues = false,
  mapToExtendedDefaultValue = false,
}: {
  sections: (GroupedPurposesBySection | GroupedPurposes)[];
  resetDefaultValues?: boolean;
  mapToExtendedDefaultValue?: boolean;
}): GroupedPurposesBySection[] =>
  sections.map((section) => {
    // Double option list only for the "Ploom Club" section
    const isPloomClubSection = section.purposeTitle?.trim().toLowerCase().includes("ploom club");
    const optionsList = isPloomClubSection ? undefined : EXTENDED_OPTIONS_LIST;

    return {
      ...section,
      optionsList,
      purposes: section.purposes.map((purpose) => {
        const accepted = "accepted" in purpose ? purpose.accepted : undefined;
        const oneOff = "one_off" in purpose ? purpose.one_off : undefined;
        return {
          ...purpose,
          ...(resetDefaultValues && { default: null }),
          ...(mapToExtendedDefaultValue && {
            default: isPloomClubSection ? `${accepted}` : `${accepted}${MULTIOPTION_SEPARATOR}${oneOff}`,
          }),
        };
      }),
    };
  });

export interface CommunicationPreferenceProps {
  className?: string;
  section: GroupedPurposesBySection;
  translationsPrefix?: string;
  formPrefix?: string;
  disabled?: boolean;
  required?: boolean;
  isColumnDisplayOfConsents?: boolean;
}

const isOptionsListKey = (value: string): value is OptionsListKey => value in optionsMapper;

const prepareDefaultConsentValue = (
  defaultValue: GroupedPurposesBySection["purposes"][number]["default"] | undefined
): string | null => {
  if (defaultValue == null) {
    return null;
  }

  return typeof defaultValue === "boolean" ? `${defaultValue}` : defaultValue;
};

export const ConsentSection = ({
  section,
  className,
  translationsPrefix = DEFAULT_TRANSLATIONS_PREFIX,
  formPrefix = "purposes",
  disabled,
  required = true,
  isColumnDisplayOfConsents = false,
}: CommunicationPreferenceProps) => {
  const { control } = useFormContext();
  const { __ } = useTranslations();
  const titleOrDescription = section.purposeTitle || section.purposeDescription;

  return (
    <div className={classNames(styles.sectionContainer, className)} data-testid={section.purposeId}>
      {titleOrDescription && (
        <div>
          {section.purposeTitle && <h2 className={styles.sectionTitle}>{section.purposeTitle}</h2>}
          <PurposeMarkdownText text={section.purposeDescription ?? ""} className={styles.sectionDescription} />
        </div>
      )}

      {section.purposes.map(({ id, name, description, default: defaultValue }) => {
        const preparedDefaultValue = prepareDefaultConsentValue(defaultValue);
        const optionsList = section.optionsList || DEFAULT_OPTIONS_LIST;

        return (
          <div key={id} className={styles.purposeGroup}>
            <PurposeMarkdownText key={`${id}-name`} text={name} className={styles.purposeTitle} />
            <PurposeMarkdownText key={`${id}-description`} text={description} className={styles.purposeSubtitle} />

            <Controller
              key={id}
              rules={{
                required: { value: required, message: __("form_validation.field_required") },
              }}
              name={`${formPrefix}.${id}`}
              control={control}
              defaultValue={preparedDefaultValue}
              render={({ field, fieldState }) => (
                <RadioButtonGroup
                  name={`${formPrefix}.${id}`}
                  orientation={isColumnDisplayOfConsents ? "vertical" : "horizontal"}
                  isError={!!fieldState.error}
                  errorText={fieldState.error?.message}
                >
                  {optionsList.filter(isOptionsListKey).map((option) => (
                    <RadioButton
                      key={optionsMapper[option]}
                      value={optionsMapper[option]}
                      label={__(`${translationsPrefix}.${option}`)}
                      checked={field.value === optionsMapper[option]}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      disabled={disabled}
                    />
                  ))}
                </RadioButtonGroup>
              )}
            />
          </div>
        );
      })}
    </div>
  );
};
